import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import DroneIcon from '../../../components/icons/DroneIcon';
import Button from '../../../components/ui/Button';

const SHOOTING_METHOD_ICONS = {
  professional_camera: { Icon: Icon, name: 'Camera' },
  mobile_camera: { Icon: Icon, name: 'Smartphone' },
  drone: { Icon: DroneIcon, name: null },
};


const ProfileHero = ({ photographer, onBookNow }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/photographers/${photographer?.id}`;
    const shareData = {
      title: photographer?.city ? `${photographer.name} — ${photographer.city}` : photographer?.name,
      text: 'احجز جلسة تصوير عبر منصة مشهد',
      url,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // المستخدم أغلق قائمة المشاركة — لا شيء
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-warm-md overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">

        {/* ─── الصورة الرئيسية ─── */}
        <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
          <Image
            src={photographer?.profileImage}
            alt={photographer?.profileImageAlt}
            className="w-full h-full object-cover"
          />
          {/* طبقة تعتيم خفيفة */}
          <div className="absolute inset-0 bg-black/20" />

          {/* بادج التقييم — أعلى يمين */}
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-md px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 border border-white/20">
            <div className="flex items-center gap-1">
              <Icon name="Star" size={16} className="text-warning fill-warning" />
              <span className="text-sm font-bold text-foreground">{photographer?.rating || '0'}</span>
            </div>
            <div className="h-3 w-px bg-border" />
            <span className="text-xs font-medium text-muted-foreground">
              {photographer?.reviewCount || 0} تقييم
            </span>
          </div>

          {/* صورة المصور الشخصية — أسفل يمين الصورة (نفس بطاقة التصفح) */}
          <div className="absolute bottom-4 right-4 z-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1e40af]/30 via-[#0891b2]/30 to-[#475569]/30 backdrop-blur-sm rounded-full blur-md" />
              <Image
                src={photographer?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(photographer?.name || 'مصور')}&background=1e40af&color=fff&size=128`}
                alt={photographer?.avatarAlt || `صورة ${photographer?.name}`}
                className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-white shadow-2xl ring-2 ring-white/50"
              />
            </div>
          </div>
        </div>

        {/* ─── معلومات المصور ─── */}
        <div className="p-6 md:p-8 flex flex-col justify-between relative">

          {/* زر المشاركة — مُثبَّت في الزاوية، لا يؤثر على layout */}
          <button
            onClick={handleShare}
            title={copied ? 'تم نسخ الرابط!' : 'مشاركة الملف'}
            className={`absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full border transition-all duration-200 z-10 ${
              copied
                ? 'bg-success/10 border-success/30 text-success'
                : 'bg-white/80 border-border text-muted-foreground hover:bg-primary/5 hover:border-primary/30 hover:text-primary'
            }`}
          >
            <Icon name={copied ? 'Check' : 'Share2'} size={16} />
          </button>

          <div>
            {/* الاسم + المدينة + الخبرة + الأيقونات */}
            <div className="mb-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                {photographer?.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm md:text-base text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Icon name="MapPin" size={18} className="text-primary" />
                  <span>{photographer?.city}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Icon name="Award" size={18} className="text-primary" />
                  <span>مستوى الخبرة: {photographer?.experienceLevelLabel || photographer?.experienceLevel || 'غير محدد'}</span>
                </div>
                {photographer?.shootingMethods?.length > 0 && (
                  <div className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm">
                    {photographer.shootingMethods.map((method) => {
                      const config = SHOOTING_METHOD_ICONS[method];
                      if (!config) return null;
                      const IconComponent = config.Icon;
                      return (
                        <span key={method} className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 text-primary border border-primary/10">
                          {config.name ? (
                            <IconComponent name={config.name} size={16} />
                          ) : (
                            <IconComponent size={16} className="text-primary" />
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
                {photographer?.portfolioUrl && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="Link" size={18} className="text-primary" />
                    <a
                      href={photographer.portfolioUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      معرض الأعمال
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* التخصصات */}
            <div className="mb-6">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-3">التخصصات</h3>
              {photographer?.specialties && photographer.specialties.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {photographer.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm text-primary text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">لم يتم تحديد التخصصات بعد</p>
              )}
            </div>

            {/* حالة الإتاحة */}
            <div className="mb-6">
              <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg border border-success/20">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <span className="text-sm font-medium text-success">متاح للحجز</span>
              </div>
            </div>
          </div>

          {/* زر الحجز */}
          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName="Calendar"
            iconPosition="right"
            onClick={onBookNow}
            className="mt-4"
          >
            احجز هذا المصور
          </Button>
        </div>

      </div>
    </div>
  );
};

export default ProfileHero;
